import { useEffect, useMemo, useState, useCallback } from "react";
import { X, Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "sonner";

// ✅ shared inputs
import { TextInput, TextArea, SelectSearch } from "../../components"; // adjust path if needed

const EditOrderSidebar = ({ isOpen, onClose, order, onOrderUpdated }) => {
  const [formData, setFormData] = useState({
    status: "pending",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    city: "",
    company_name: "",
    comments: "",
  });

  const [loading, setLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  /* -------------------- helpers -------------------- */

  // ✅ normalizes: event -> value, option -> option.value, raw -> raw
  const normalizeValue = (v) => {
    if (v && typeof v === "object") {
      // React change event
      if ("target" in v && v.target) return v.target.value ?? "";
      // Select option object {value,label}
      if ("value" in v) return v.value ?? "";
    }
    return v ?? "";
  };

  const setField = (name, v) => {
    const value = normalizeValue(v);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetState = () => {
    setFormData({
      status: "pending",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      city: "",
      company_name: "",
      comments: "",
    });
    setCart([]);
    setSearchQuery("");
  };

  /* -------------------- lock scroll -------------------- */
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => (document.body.style.overflow = "unset");
  }, [isOpen]);

  /* -------------------- fetch products -------------------- */
  const getProducts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(`/products/search`);
      setAvailableProducts(res?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error fetching products");
    } finally {
      setLoading(false);
    }
  }, []);

  /* -------------------- init when open/order changes -------------------- */
  useEffect(() => {
    if (!isOpen) return;

    getProducts();

    if (order) {
      setFormData({
        status: order.status || "pending",
        first_name: order.first_name || "",
        last_name: order.last_name || "",
        email: order.email || "",
        phone: order.phone || "",
        city: order.city || "",
        company_name: order.company_name || "",
        comments: order.comments || "",
      });

      const safeProducts = Array.isArray(order.products) ? order.products : [];
      setCart(
        safeProducts.map((p) => ({
          ...p,
          pivot: {
            ...(p.pivot || {}),
            quantity: Number(p.pivot?.quantity || 1),
            removed: !!p.pivot?.removed,
          },
        }))
      );
    } else {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, isOpen, getProducts]);

  /* -------------------- cart ops -------------------- */
  const updateQuantity = (productId, newQuantity) => {
    const qty = Number(newQuantity);
    if (Number.isNaN(qty)) return;

    if (qty < 1) {
      setCart((prev) =>
        prev.map((item) =>
          item.id === productId
            ? {
                ...item,
                pivot: { ...(item.pivot || {}), quantity: 0, removed: true },
              }
            : item
        )
      );
      return;
    }

    setCart((prev) =>
      prev.map((item) =>
        item.id === productId
          ? { ...item, pivot: { ...(item.pivot || {}), quantity: qty } }
          : item
      )
    );
  };

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        const currentQty = Number(existing.pivot?.quantity || 1);
        return prev.map((p) =>
          p.id === product.id
            ? { ...p, pivot: { ...(p.pivot || {}), quantity: currentQty + 1, removed: false } }
            : p
        );
      }
      return [...prev, { ...product, pivot: { quantity: 1, removed: false } }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === productId
          ? { ...item, pivot: { ...(item.pivot || {}), quantity: 0, removed: true } }
          : item
      )
    );
  };

  /* -------------------- submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!order?.id) return toast.error("Order not found");

    setLoading(true);
    try {
      const updatedProducts = cart.map((product) => ({
        id: product.id,
        quantity: Number(product.pivot?.quantity || 0),
        remove: !!product.pivot?.removed,
      }));

      const res = await axiosInstance.put(`/orders/update/${order.id}`, {
        ...formData,
        products: updatedProducts,
      });

      toast.success("Order updated successfully");
      onOrderUpdated?.(res.data);

      onClose?.();
      resetState();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating order");
      console.error("Error updating order:", error);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- memos -------------------- */
const statusOptions = useMemo(
  () => [
    { label: "Nouvelle", value: "pending" },

    { label: "Confirmée", value: "confirmed" },
    { label: "En préparation", value: "preparing" },
    { label: "Remise au transporteur", value: "shipped" },
    { label: "Livrée au client", value: "delivered" },
    { label: "Annulée", value: "cancelled" },
    { label: "Refusée", value: "refused" },
    { label: "Pas de réponse", value: "no_answer" },
  ],
  []
);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableProducts;
    return availableProducts.filter((p) =>
      String(p.name || "").toLowerCase().includes(q)
    );
  }, [availableProducts, searchQuery]);

  /* -------------------- render -------------------- */
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              onClose?.();
              resetState();
            }}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="fixed inset-y-0 right-0 w-full lg:w-2/5 bg-white dark:bg-neutral-900 border-l border-neutral-100 dark:border-neutral-800 z-50 overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-neutral-800 dark:text-white">
                  Edit Order
                </h2>

                <button
                  onClick={() => {
                    onClose?.();
                    resetState();
                  }}
                  className="text-neutral-500 hover:text-neutral-700 dark:hover:text-white"
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Status */}
                <SelectSearch
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={(v) => setField("status", v)} // ✅ works for value OR {value,label}
                  options={statusOptions}
                  placeholder="Select status"
                />

                {/* Customer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={(v) => setField("first_name", v)} // ✅ event-safe
                    placeholder="First name"
                  />
                  <TextInput
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={(v) => setField("last_name", v)}
                    placeholder="Last name"
                  />
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(v) => setField("email", v)} // ✅ FIXES [object Object]
                    placeholder="email@example.com"
                  />
                  <TextInput
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(v) => setField("phone", v)}
                    placeholder="+212..."
                  />
                  <TextInput
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={(v) => setField("city", v)}
                    placeholder="City"
                  />
                  <TextInput
                    label="Company Name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={(v) => setField("company_name", v)}
                    placeholder="Company (optional)"
                  />
                </div>

                {/* Comments */}
                <TextArea
                  label="Comments"
                  name="comments"
                  value={formData.comments}
                  onChange={(v) => setField("comments", v)} // ✅ event-safe
                  rows={3}
                  placeholder="Notes about the order..."
                />

                {/* Products in cart */}
                <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-neutral-800 dark:text-white">
                      Products in Cart
                    </p>
                    <p className="text-xs text-neutral-500">
                      {cart.filter((p) => !p.pivot?.removed).length} items
                    </p>
                  </div>

                  <ul className="space-y-2">
                    {cart.map((product) => {
                      if (product.pivot?.removed) return null;

                      const qty = Number(product.pivot?.quantity || 1);

                      return (
                        <li
                          key={product.id}
                          className="flex items-center gap-3 bg-neutral-50 dark:bg-neutral-800 p-2 rounded-xl"
                        >
                          <img
                            src={product.images?.[0] || "/placeholder.svg"}
                            alt={product.name}
                            className="w-14 h-14 object-cover rounded-lg"
                          />

                          <div className="flex flex-col items-start flex-1">
                            <p className="font-semibold text-sm text-neutral-900 dark:text-white">
                              {product.name}
                            </p>

                            <div className="flex items-center mt-2">
                              <button
                                type="button"
                                onClick={() => updateQuantity(product.id, qty - 1)}
                                className="p-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-lg"
                              >
                                <Minus size={16} />
                              </button>

                              <input
                                type="number"
                                min={1}
                                value={qty}
                                onChange={(e) =>
                                  updateQuantity(
                                    product.id,
                                    Number.parseInt(e.target.value || "1", 10)
                                  )
                                }
                                className="w-16 text-center mx-2 rounded-lg bg-white dark:bg-neutral-900 px-2 py-1 text-neutral-900 dark:text-white border border-neutral-200 dark:border-neutral-700 outline-none"
                              />

                              <button
                                type="button"
                                onClick={() => updateQuantity(product.id, qty + 1)}
                                className="p-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-lg"
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFromCart(product.id)}
                            className="text-red-500 hover:text-red-700"
                            aria-label="Remove from cart"
                          >
                            <X size={20} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Available products */}
                <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 p-4">
                  <p className="text-sm font-medium text-neutral-800 dark:text-white mb-3">
                    Available Products
                  </p>

                  <TextInput
                    label={null}
                    name="search_products"
                    value={searchQuery}
                    onChange={(v) => setSearchQuery(normalizeValue(v))} // ✅ event-safe
                    placeholder="Search products..."
                  />

                  <ul className="space-y-2 mt-3 max-h-64 overflow-y-auto pr-1">
                    {filteredProducts.map((product) => (
                      <li
                        key={product.id}
                        className="flex items-center gap-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-xl"
                      >
                        <img
                          src={product.images?.[0] || "/placeholder.svg"}
                          alt={product.name}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-neutral-900 dark:text-white">
                            {product.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => addToCart(product)}
                          className="p-2 bg-cyan-400 text-white rounded-lg hover:bg-[#2B2B35] transition-colors"
                        >
                          <Plus size={18} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm text-white bg-cyan-400 rounded-lg hover:bg-[#2B2B35] disabled:opacity-60"
                  >
                    {loading ? "Updating Order ..." : "Update Order"}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditOrderSidebar;